import { useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import { TextSelection } from "@tiptap/pm/state";

import {
    CheckIcon,
    ClearIcon,
    LinkIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";

import { LinkEditorProps } from "./types";

const LinkEditor = ({ editor, editorState, isDisabled }: LinkEditorProps) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const [url, setUrl] = useState("");
    const [text, setText] = useState("");

    const openLink = () => {
        if (editorState.isLink) {
            editor
                .chain()
                .focus()
                .unsetMark("link")
                .command(({ tr }) => {
                    tr.removeStoredMark(editor.schema.marks.link);
                    return true;
                })
                .run();
        } else {
            const highlighted = editor.state.selection.empty
                ? ""
                : editor.state.doc.textBetween(
                      editor.state.selection.from,
                      editor.state.selection.to,
                      " "
                  );

            setText(highlighted);
            setIsOpen(true);
        }
    };

    const applyLink = () => {
        if (!url) {
            reset();
            return;
        }

        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .insertContent({
                type: "text",
                text: text || url,
                marks: [
                    {
                        type: "link",
                        attrs: { href: url }
                    }
                ]
            })
            .setMeta("preventAutolink", true)
            .unsetMark("link")
            .run();

        setIsOpen(false);
        setUrl("");
        setText("");
    };

    const reset = () => {
        editor
            .chain()
            .focus()
            .command(({ tr, state }) => {
                const { $to } = state.selection;

                tr.setSelection(TextSelection.create(tr.doc, $to.pos));
                return true;
            })
            .run();

        setIsOpen(false);
        setUrl("");
        setText("");
    };

    return (
        <div>
            {isOpen ? (
                <div
                    css={css({
                        display: "flex",
                        alignItems: "center",
                        columnGap: "8px",

                        "@media (max-width: 599px)": {
                            flexWrap: "wrap",
                            rowGap: "8px"
                        }
                    })}
                >
                    <InputField
                        autoFocus={editor.state.selection.empty}
                        size={INPUT_FIELD_SIZES.Small}
                        placeholder={t("Banner##url")}
                        value={url}
                        onChange={e => setUrl(e.target.value)}
                    />

                    <InputField
                        size={INPUT_FIELD_SIZES.Small}
                        placeholder={t("Banner##text")}
                        value={text}
                        onChange={e => setText(e.target.value)}
                    />

                    <TooltipGeneral
                        placement={TOOLTIP_PLACEMENT.Top}
                        title={t("Banner##close link")}
                    >
                        <Button
                            color={BUTTON_COLORS.Secondary}
                            size={BUTTON_SIZES.Small}
                            variant={BUTTON_VARIANTS.IconOnly}
                            icon={<ClearIcon />}
                            onClick={reset}
                        />
                    </TooltipGeneral>

                    <TooltipGeneral
                        placement={TOOLTIP_PLACEMENT.Top}
                        title={t("Banner##apply link")}
                    >
                        <Button
                            color={BUTTON_COLORS.PrimarySubtle}
                            size={BUTTON_SIZES.Small}
                            variant={BUTTON_VARIANTS.IconOnly}
                            icon={<CheckIcon />}
                            onClick={applyLink}
                        />
                    </TooltipGeneral>
                </div>
            ) : (
                <TooltipGeneral
                    placement={TOOLTIP_PLACEMENT.Top}
                    title={t("Banner##link")}
                >
                    <span>
                        <Button
                            color={
                                editorState.isLink
                                    ? BUTTON_COLORS.Primary
                                    : BUTTON_COLORS.White
                            }
                            size={BUTTON_SIZES.Small}
                            variant={BUTTON_VARIANTS.IconOnly}
                            icon={<LinkIcon />}
                            disabled={isDisabled ?? !editorState.canLink}
                            onClick={openLink}
                        />
                    </span>
                </TooltipGeneral>
            )}
        </div>
    );
};

export { LinkEditor };
